/**
 * URL registry for the candidate-facing interview app (`InterviewApp.tsx`,
 * selected when `REACT_APP_SALV_APP === 'interview'`).
 *
 * These routes are deliberately NOT part of `src/url-registry/` and are never
 * synced into the staffing-chat catalog
 * (server/staffing_chat/app_routes.generated.json). They are token-gated
 * candidate surfaces — segments like `:encodedId`, `:encodedTestData` and
 * `:token` embed per-candidate secrets, so the chat agent must never learn
 * these URL shapes or navigate recruiters to them.
 *
 * That boundary is also why entries here carry only a path: no `label`,
 * `description`, `query_params`, or `llm_navigable`. The recruiter registry
 * carries that metadata *for the chat agent*, so writing it here would author
 * exactly the artifact this file exists to prevent.
 * `noChatCatalogLeak.test.ts` asserts both halves of that boundary.
 *
 * Every route the candidate router mounts is declared here.
 * `src/candidate-app/routeMounts.tsx` pairs each key with the component that
 * mounts at it, and `src/candidate-app/createCandidateRouter.tsx` generates the
 * React Router tree from the two — there is no second path string in
 * `InterviewApp.tsx`.
 *
 * Unlike `src/url-registry/`, this is NOT the end-to-end source of truth for
 * these URLs: `server/caller/web_call/web_call_link_helpers.py` and
 * `server/hm_intake/*` construct candidate links independently, so editing a
 * template here does not change the links the backend sends. This registry is
 * the source of truth for the frontend router and in-app navigation only.
 *
 * `candidateMountPaths` holds the router mount for the two routes whose mount
 * takes an optional param (`/opt-out/success/:encodedId?`). It is kept apart
 * from `candidatePaths` so an optional template can never reach `buildPath`,
 * whose substitution regex does not model `?` and would emit a literal
 * `:encodedId?` segment without throwing.
 */

// `as const` keeps each template's string literal so `PathParams<typeof
// candidatePaths.RESCHEDULE>` extracts `:interviewId`.
export const candidatePaths = {
  // ---------------------------------------------------------------------------
  // Canonical interview surfaces
  //
  // - Canonical candidate app routes live under `/web-interview/*`.
  // - `/web-call/*` exists as a backwards-compatible alias because some legacy
  //   comms still generate those URLs. We should continue generating
  //   `/web-interview/*` going forward.
  //
  // Link *types* (server-side) are resolved by `/check_web_link`:
  // - Individual link (`WebCallLink`): maps to a specific candidate + call_id.
  // - Shared link (`SharedWebCallLink`): campaign-wide link; call_id/candidate_id
  //   are created only after the candidate submits the form.
  //
  // Scheduling is a separate feature ("scheduled AI video calls"):
  // - If scheduling is enabled for the job and the call is not yet scheduled,
  //   the UI can route candidates into `/web-interview/:encodedId/schedule`.
  // - This is **not** the same concept as "scheduled follow-up interviews"
  //   (human interviews) elsewhere in the product. These pages are strictly for
  //   scheduling the AI video call.
  // ---------------------------------------------------------------------------

  WEB_INTERVIEW_SESSION: '/web-interview/session',
  WEB_INTERVIEW_SESSION_REJOIN: '/web-interview/session/rejoin',
  WEB_INTERVIEW_SESSION_SCHEDULE: '/web-interview/session/schedule',
  WEB_INTERVIEW_SESSION_SCHEDULE_CONFIRM:
    '/web-interview/session/schedule/confirm',
  WEB_INTERVIEW_SESSION_SCHEDULE_SCHEDULED:
    '/web-interview/session/schedule/scheduled',
  FEEDBACK_SESSION: '/feedback/session',
  FEEDBACK_SESSION_SUCCESS: '/feedback/session/success',
  WEB_INTERVIEW_TEST_CALL_REJOIN:
    '/web-interview/test-call/:encodedTestData/rejoin',
  WEB_INTERVIEW_TEST_CALL: '/web-interview/test-call/:encodedTestData',
  WEB_INTERVIEW_PRACTICE: '/web-interview/practice/:encodedPracticeData',
  WEB_INTERVIEW_REJOIN: '/web-interview/:encodedId/rejoin',
  WEB_INTERVIEW: '/web-interview/:encodedId',
  WEB_INTERVIEW_SCHEDULE: '/web-interview/:encodedId/schedule',
  WEB_INTERVIEW_SCHEDULE_CONFIRM: '/web-interview/:encodedId/schedule/confirm',
  WEB_INTERVIEW_SCHEDULE_SCHEDULED:
    '/web-interview/:encodedId/schedule/scheduled',

  // ---------------------------------------------------------------------------
  // Backwards-compatible `/web-call/*` aliases. `:encodedId` is reused verbatim
  // so WebCallPage and the credential derivation in `candidateCapability` keep
  // working.
  // ---------------------------------------------------------------------------

  WEB_CALL_LEGACY: '/web-call/:encodedId',
  WEB_CALL_LEGACY_SCHEDULE: '/web-call/:encodedId/schedule',
  WEB_CALL_LEGACY_SCHEDULE_CONFIRM: '/web-call/:encodedId/schedule/confirm',
  WEB_CALL_LEGACY_SCHEDULE_SCHEDULED: '/web-call/:encodedId/schedule/scheduled',
  WEB_CALL_LEGACY_FEEDBACK: '/web-call/:encodedId/feedback',

  // ---------------------------------------------------------------------------
  // Rescheduling a human follow-up interview
  // ---------------------------------------------------------------------------

  RESCHEDULE: '/reschedule/:interviewId',
  RESCHEDULE_CALENDAR: '/reschedule/:interviewId/calendar',

  // ---------------------------------------------------------------------------
  // Static interview outcomes and permission help
  // ---------------------------------------------------------------------------

  WEB_INTERVIEW_ERROR: '/web-interview/error',
  WEB_INTERVIEW_COMPLETED: '/web-interview/completed',
  WEB_INTERVIEW_VIDEO_HELP: '/web-interview/video-help',
  WEB_INTERVIEW_SCREEN_SHARE_HELP: '/web-interview/screen-share-help',

  // ---------------------------------------------------------------------------
  // Post-interview feedback
  // ---------------------------------------------------------------------------

  FEEDBACK_SUCCESS: '/feedback/:encodedId/success',
  FEEDBACK_TEST_CALL_SUCCESS: '/feedback/test-call/:encodedTestData/success',
  FEEDBACK: '/feedback/:encodedId',
  FEEDBACK_TEST_CALL: '/feedback/test-call/:encodedTestData',

  // ---------------------------------------------------------------------------
  // Opt-out / accommodation confirmations. Both mount an optional id segment
  // (see `candidateMountPaths`); the template here stays the base path so
  // `buildPath` never sees the `?`.
  // ---------------------------------------------------------------------------

  OPT_OUT_SUCCESS: '/opt-out/success',
  ACCOMMODATION_REQUEST_SUCCESS: '/accommodation-request/success',

  PRACTICE_COMPLETED: '/practice-completed',

  // ---------------------------------------------------------------------------
  // Token-gated one-shot landing pages reached from candidate emails
  // ---------------------------------------------------------------------------

  REJECTION_REVIEW: '/rejection-review/:action',
  CANDIDATE_RESUME_FEEDBACK_DETAILS:
    '/candidate/resume-feedback-details/:token',
  CANDIDATE_WITHDRAW: '/candidate/withdraw/:token',
  CANDIDATE_UNINTERESTED: '/candidate/uninterested/:token',
  CANDIDATE_ALTERNATIVE_ROLE: '/candidate/alternative-role/:token',
  CANDIDATE_PREAPPLY_OPT_OUT: '/candidate/preapply-opt-out/:token',

  HM_INTAKE: '/hm-intake/:linkId',
} as const;

export type CandidateRouteKey = keyof typeof candidatePaths;

function isCandidateRouteKey(key: string): key is CandidateRouteKey {
  return Object.hasOwn(candidatePaths, key);
}

/** Every key of `candidatePaths`, in declaration order. */
export const candidateRouteKeys: readonly CandidateRouteKey[] =
  Object.keys(candidatePaths).filter(isCandidateRouteKey);

/** Router mounts that differ from the navigable template in `candidatePaths`. */
const candidateMountPaths: Partial<Record<CandidateRouteKey, string>> = {
  OPT_OUT_SUCCESS: '/opt-out/success/:encodedId?',
  ACCOMMODATION_REQUEST_SUCCESS: '/accommodation-request/success/:encodedId?',
};

/** The pattern the candidate router mounts `key` at. */
export function candidateMountPath(key: CandidateRouteKey): string {
  return candidateMountPaths[key] ?? candidatePaths[key];
}
